import { Check, Plane, ShieldCheck, Info, Crown } from "lucide-react";

const PACKAGES = [
  {
    id: "5_reels",
    label: "5 Reels",
    subtitle: "Mindestabnahme",
    price: "300€",
    perReel: "60€ / Reel netto",
    discount: null,
    features: ["5 fertig geschnittene Reels", "Hook · Cut · Sound", "Lieferung in 7–14 Tagen"],
    highlight: false,
    badge: null,
  },
  {
    id: "10_reels",
    label: "10 Reels",
    subtitle: "Smart Choice",
    price: "570€",
    perReel: "57€ / Reel netto",
    discount: "5% Rabatt",
    features: ["10 fertig geschnittene Reels", "Strategische Hook-Beratung", "2 Überarbeitungen inklusive"],
    highlight: false,
    badge: null,
  },
  {
    id: "20_reels",
    label: "20 Reels",
    subtitle: "Maximum Value",
    price: "1.020€",
    perReel: "51€ / Reel netto",
    discount: "15% Rabatt",
    features: [
      "20 fertig geschnittene Reels",
      "Content-Plan nach Performance-Prinzip",
      "Priorisierter Support",
      "Hook- & Skript-Beratung inklusive",
    ],
    highlight: true,
    badge: "Most Popular · Best Value",
  },
];

export default function Pricing({ onSelectPackage }) {
  return (
    <section
      className="relative border-b border-mfp-gold/10 overflow-hidden"
      data-testid="pricing-section"
    >
      {/* Atmospheric gold glow */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[60rem] h-[40rem] bg-mfp-gold/[0.06] blur-[140px] rounded-full" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 md:px-8 py-14 md:py-24">
        <div className="mb-12 md:mb-16 text-center md:text-left">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-mfp-gold font-bold mb-3">
            <span className="block h-px w-6 bg-mfp-gold" />
            Investment
          </div>
          <h2 className="font-display text-3xl md:text-5xl uppercase leading-[1.05] text-mfp-champagne">
            Transparente Pakete.<br />
            <span className="text-mfp-gold">Premium Ergebnisse.</span>
          </h2>
          <p className="mt-4 text-mfp-muted text-base md:text-lg max-w-2xl mx-auto md:mx-0">
            Mindestabnahme: 5 Reels. Staffelrabatte ab 10 Reels. Alle Preise netto.
            Keine versteckten Kosten.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6 items-stretch">
          {PACKAGES.map((p) => (
            <PricingCard key={p.id} pkg={p} onSelect={onSelectPackage} />
          ))}
        </div>

        {/* Drone Add-on */}
        <div
          className="mt-8 md:mt-10 relative border border-mfp-gold/15 bg-white/[0.02] backdrop-blur-sm p-6 md:p-8 flex flex-col md:flex-row gap-5 md:gap-8 md:items-center rounded-2xl"
          data-testid="drone-addon"
        >
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 bg-gradient-to-br from-mfp-gold to-mfp-goldDeep flex items-center justify-center shrink-0 rounded-xl shadow-[0_8px_20px_-8px_rgba(200,169,107,0.5)]">
              <Plane className="h-6 w-6 text-mfp-bg" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-mfp-muted">Add-On</div>
              <div className="font-display text-2xl uppercase text-mfp-champagne">Drohnen-Aufnahme</div>
            </div>
          </div>
          <div className="font-display text-3xl md:text-4xl md:ml-auto text-mfp-champagne">
            +30€ <span className="text-sm text-mfp-muted">netto / Einsatz</span>
          </div>
        </div>

        <div className="mt-5 grid md:grid-cols-2 gap-3 text-xs md:text-sm text-mfp-muted">
          <div className="flex items-start gap-2 border border-mfp-gold/10 p-4 rounded-xl bg-white/[0.015]">
            <Info className="h-4 w-4 text-mfp-gold shrink-0 mt-0.5" />
            <p>
              Drohnenflüge nur erlaubt, wenn gesetzlich zulässig. Genehmigungen
              müssen vom Kunden selbst eingeholt und bezahlt werden.
            </p>
          </div>
          <div className="flex items-start gap-2 border border-mfp-gold/10 p-4 rounded-xl bg-white/[0.015]">
            <ShieldCheck className="h-4 w-4 text-mfp-gold shrink-0 mt-0.5" />
            <p>
              Drohne ist über MediaFly Production haftpflichtversichert.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PricingCard({ pkg, onSelect }) {
  const isHighlight = pkg.highlight;
  return (
    <div
      className={`relative group rounded-2xl p-[1px] transition-all duration-300 ${
        isHighlight
          ? "bg-gradient-to-br from-mfp-gold via-mfp-goldLight to-mfp-goldDeep shadow-[0_30px_80px_-20px_rgba(200,169,107,0.45)] lg:-translate-y-2 hover:-translate-y-3"
          : "bg-mfp-gold/15 hover:bg-mfp-gold/30 hover:-translate-y-1"
      }`}
      data-testid={`pricing-card-${pkg.id}`}
    >
      {/* Hover glow */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 rounded-2xl blur-2xl transition-opacity duration-500 ${
          isHighlight
            ? "bg-mfp-gold/25 opacity-100"
            : "bg-mfp-gold/0 group-hover:bg-mfp-gold/15 group-hover:opacity-100 opacity-0"
        } -z-10`}
      />

      <div
        className={`relative h-full rounded-2xl p-6 md:p-8 flex flex-col gap-6 backdrop-blur-sm ${
          isHighlight ? "bg-mfp-surface/95" : "bg-mfp-surface/80"
        }`}
      >
        {pkg.badge && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-gradient-to-r from-mfp-gold to-mfp-goldLight text-mfp-bg text-[10px] uppercase tracking-[0.18em] font-bold px-4 py-1.5 rounded-full shadow-[0_8px_20px_-8px_rgba(200,169,107,0.7)] whitespace-nowrap">
            <Crown className="h-3 w-3" />
            {pkg.badge}
          </div>
        )}

        <div className="flex items-baseline justify-between mt-1">
          <h3 className="font-display text-2xl md:text-3xl uppercase text-mfp-champagne tracking-tight">
            {pkg.label}
          </h3>
          <span className="text-[10px] md:text-xs uppercase tracking-widest text-mfp-gold/80 font-bold">
            {pkg.subtitle}
          </span>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-5xl md:text-6xl text-mfp-champagne leading-none tracking-tight">
              {pkg.price}
            </span>
            <span className="text-xs text-mfp-muted">netto</span>
          </div>
          <div className="text-sm text-mfp-muted font-medium">{pkg.perReel}</div>
          {pkg.discount && (
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest bg-mfp-gold/15 text-mfp-gold px-2.5 py-1 font-bold rounded-md border border-mfp-gold/20">
              {pkg.discount}
            </div>
          )}
        </div>

        <ul className="space-y-2.5 text-sm text-mfp-champagne/90 flex-1">
          {pkg.features.map((f, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span className="flex-shrink-0 mt-0.5 h-4 w-4 rounded-full bg-mfp-gold/15 flex items-center justify-center">
                <Check className="h-3 w-3 text-mfp-gold" strokeWidth={3} />
              </span>
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() => onSelect?.(pkg.id)}
          className={`mfp-btn group/btn relative w-full overflow-hidden text-sm px-5 py-3.5 rounded-xl transition-all ${
            isHighlight
              ? "bg-gradient-to-r from-mfp-gold to-mfp-goldLight text-mfp-bg hover:brightness-110 shadow-[0_10px_30px_-10px_rgba(200,169,107,0.6)]"
              : "bg-mfp-bg/60 border border-mfp-gold/30 text-mfp-champagne hover:bg-mfp-gold hover:text-mfp-bg hover:border-mfp-gold"
          }`}
          data-testid={`pricing-cta-${pkg.id}`}
        >
          <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700" />
          <span className="relative">{pkg.label} auswählen</span>
        </button>
      </div>
    </div>
  );
}
